/**
 * EventAlbumDialog — Feb 2026
 *
 * Create OR edit an event album. On create, walks the user through:
 *  1. Title + date + location + audience,
 *  2. (After save) media upload with drag-and-drop + progress rows,
 *  3. Publish button (only enabled once at least one media exists).
 * On edit, shows the same form pre-filled and lets HR change audience
 * / add or delete media / republish.
 */
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Upload, X, Loader2, Rocket, Camera, Film, CheckCircle2 } from "lucide-react";
import { mediaSrc } from "@/lib/authQuery";

const MAX_PHOTO_MB = 15;
const MAX_VIDEO_MB = 200;

export default function EventAlbumDialog({ wsId, album, onClose, onSaved }) {
  const isEdit = !!album?.id;
  const [title, setTitle] = useState(album?.title || "");
  const [description, setDescription] = useState(album?.description || "");
  const [eventDate, setEventDate] = useState(album?.event_date || new Date().toISOString().slice(0, 10));
  const [location, setLocation] = useState(album?.location || "");
  const [audienceType, setAudienceType] = useState(album?.audience_type || "all");
  const [deptIds, setDeptIds] = useState(album?.department_ids || []);
  const [locIds, setLocIds] = useState(album?.location_ids || []);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [savedId, setSavedId] = useState(album?.id || null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState([]);
  const [media, setMedia] = useState([]);
  const [status, setStatus] = useState(album?.status || "draft");
  const fileRef = useRef(null);

  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/departments`).then((r) => setDepartments(r.data || [])).catch(() => {});
    api.get(`/workspaces/${wsId}/locations`).then((r) => setLocations(r.data || [])).catch(() => {});
    if (savedId) refreshMedia(savedId);
    // eslint-disable-next-line
  }, [wsId, savedId]);

  const refreshMedia = async (aid) => {
    try {
      const { data } = await api.get(`/event-albums/${aid}`);
      setMedia(data?.media || []);
      setStatus(data?.status || "draft");
    } catch { /* ignore */ }
  };

  const saveMeta = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    try {
      const payload = {
        title, description, event_date: eventDate, location,
        audience_type: audienceType,
        department_ids: audienceType === "departments" ? deptIds : [],
        location_ids: audienceType === "locations" ? locIds : [],
      };
      if (isEdit || savedId) {
        await api.patch(`/event-albums/${savedId || album.id}`, payload);
        toast.success("Saved");
      } else {
        const { data } = await api.post(`/workspaces/${wsId}/event-albums`, payload);
        setSavedId(data.id);
        toast.success("Album created — add photos now");
      }
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  const doUpload = async (files) => {
    if (!savedId) { toast.error("Save album details first"); return; }
    for (const f of files) {
      const isVideo = f.type.startsWith("video/");
      const capMB = isVideo ? MAX_VIDEO_MB : MAX_PHOTO_MB;
      if (f.size > capMB * 1024 * 1024) {
        toast.error(`${f.name}: exceeds ${capMB} MB limit`);
        continue;
      }
      const rowId = crypto.randomUUID();
      setUploading((prev) => [...prev, { id: rowId, name: f.name, pct: 0, kind: isVideo ? "video" : "photo" }]);
      try {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/event-albums/${savedId}/media`, fd, {
          onUploadProgress: (e) => {
            const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
            setUploading((prev) => prev.map((r) => r.id === rowId ? { ...r, pct } : r));
          },
        });
        setUploading((prev) => prev.map((r) => r.id === rowId ? { ...r, pct: 100 } : r));
        setTimeout(() => setUploading((prev) => prev.filter((r) => r.id !== rowId)), 800);
      } catch (e) {
        toast.error(apiErrorMessage(e, `${f.name}: upload failed`));
        setUploading((prev) => prev.filter((r) => r.id !== rowId));
      }
    }
    await refreshMedia(savedId);
  };

  const onPick = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    doUpload(files);
  };
  const onDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files || []);
    doUpload(files);
  };

  const removeMedia = async (m) => {
    if (!window.confirm(`Remove "${m.filename || 'file'}"?`)) return;
    try {
      await api.delete(`/event-media/${m.id}`);
      await refreshMedia(savedId);
      toast.success("Removed");
    } catch (e) { toast.error(apiErrorMessage(e, "Remove failed")); }
  };

  const publish = async () => {
    if (!savedId) return;
    setBusy(true);
    try {
      await api.post(`/event-albums/${savedId}/publish`);
      toast.success("🎉 Album published to workspace!");
      onSaved?.({ id: savedId });
    } catch (e) { toast.error(apiErrorMessage(e, "Publish failed")); }
    finally { setBusy(false); }
  };

  const closeAndSave = () => {
    if (savedId) onSaved?.({ id: savedId });
    else onClose?.();
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) closeAndSave(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="event-album-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit event album" : (savedId ? "Add media to album" : "New event album")}</DialogTitle>
          <DialogDescription>
            {savedId
              ? "Drop photos / videos below. First upload becomes the cover — tap ⭐ on any tile to change it."
              : "Give this event a name, when it happened, and who should see it. You'll upload media in the next step."}
          </DialogDescription>
        </DialogHeader>

        {/* Meta form */}
        <div className="space-y-3">
          <div><Label>Title *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="event-title-input" className="h-10" placeholder="e.g. Diwali Party 2026" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Event date</Label><Input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} data-testid="event-date-input" className="h-10" /></div>
            <div><Label>Location</Label><Input value={location} onChange={(e) => setLocation(e.target.value)} data-testid="event-location-input" className="h-10" placeholder="HQ · Bengaluru" /></div>
          </div>
          <div><Label>Description</Label>
            <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="event-description-input" placeholder="A few words for the album cover" />
          </div>
          <div>
            <Label>Audience</Label>
            <Select value={audienceType} onValueChange={setAudienceType}>
              <SelectTrigger className="h-10" data-testid="event-audience-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Everyone in workspace</SelectItem>
                <SelectItem value="departments">Specific departments</SelectItem>
                <SelectItem value="locations">Specific locations</SelectItem>
              </SelectContent>
            </Select>
            {audienceType === "departments" && (
              <MultiPicker items={departments} value={deptIds} onChange={setDeptIds} label="Departments" testid="event-dept-picker" />
            )}
            {audienceType === "locations" && (
              <MultiPicker items={locations} value={locIds} onChange={setLocIds} label="Locations" testid="event-loc-picker" />
            )}
          </div>
          <Button onClick={saveMeta} disabled={busy} className="rounded-none w-full sm:w-auto" data-testid="event-save-meta">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            {savedId ? "Save details" : "Save & continue to upload"}
          </Button>
        </div>

        {/* Media panel — visible once album is saved */}
        {savedId && (
          <>
            <hr className="my-3 border-border" />
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base">Photos & videos <span className="text-xs text-muted-foreground">({media.length})</span></Label>
                <input ref={fileRef} type="file" accept="image/*,video/*" multiple hidden onChange={onPick} data-testid="event-file-input" />
                <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="event-upload-btn">
                  <Upload className="w-3.5 h-3.5 mr-1.5" /> Upload
                </Button>
              </div>
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={onDrop}
                className="border-2 border-dashed border-border rounded-none p-4 text-center bg-muted/30 hover:bg-muted/50 transition-colors cursor-pointer"
                onClick={() => fileRef.current?.click()}
                data-testid="event-drop-zone"
              >
                <Upload className="w-6 h-6 mx-auto text-muted-foreground" />
                <div className="text-sm mt-1">Drag photos & videos here — or click to browse</div>
                <div className="text-[10px] text-muted-foreground mt-1">Photos ≤ {MAX_PHOTO_MB} MB · Videos ≤ {MAX_VIDEO_MB} MB · Up to 50 photos + 10 videos</div>
              </div>

              {/* Uploading progress rows */}
              {uploading.length > 0 && (
                <div className="mt-2 space-y-1" data-testid="event-upload-progress">
                  {uploading.map((u) => (
                    <div key={u.id} className="text-[11px] flex items-center gap-2">
                      {u.kind === "video" ? <Film className="w-3 h-3" /> : <Camera className="w-3 h-3" />}
                      <span className="flex-1 truncate">{u.name}</span>
                      <span className="text-muted-foreground">{u.pct}%</span>
                      <div className="w-24 h-1 bg-muted overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${u.pct}%` }} /></div>
                    </div>
                  ))}
                </div>
              )}

              {/* Current media thumbnails */}
              {media.length > 0 && (
                <div className="mt-3 grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="event-media-grid">
                  {media.map((m) => (
                    <div key={m.id} className="relative aspect-square border border-border overflow-hidden group">
                      {m.kind === "photo" ? (
                        <img src={mediaSrc(m.url)} alt={m.filename} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center bg-black text-white">
                          <Film className="w-6 h-6" />
                        </div>
                      )}
                      <button
                        onClick={() => removeMedia(m)}
                        className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center bg-red-500/90 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`event-media-remove-${m.id}`}
                        aria-label="Remove"
                      ><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        <DialogFooter className="gap-2 mt-4">
          <Button variant="outline" onClick={closeAndSave} data-testid="event-dialog-close">Close</Button>
          {savedId && status !== "published" && (
            <Button
              onClick={publish}
              disabled={busy || media.length === 0}
              className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white rounded-none"
              data-testid="event-publish-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Rocket className="w-4 h-4 mr-2" />}
              Publish to workspace
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MultiPicker({ items, value, onChange, label, testid }) {
  const toggle = (id) => onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  return (
    <div className="mt-2" data-testid={testid}>
      <div className="text-[11px] text-muted-foreground mb-1">{label} · pick one or more</div>
      <div className="flex flex-wrap gap-1">
        {items.map((it) => (
          <button
            key={it.id}
            type="button"
            onClick={() => toggle(it.id)}
            className={`text-xs px-2 py-1 border ${value.includes(it.id) ? "bg-primary text-primary-foreground border-primary" : "border-border bg-background"}`}
          >{it.name}</button>
        ))}
        {items.length === 0 && <div className="text-xs text-muted-foreground">None yet.</div>}
      </div>
    </div>
  );
}
